import type { NotepadGrid, NotepadMode, Seat } from './types';

/**
 * The elimination note.
 *
 * Three modes, which are not three implementations:
 *
 *   Manual  you mark everything yourself, as on paper
 *   Smart   fills in what you were plainly told -- your hand, and cards ruled
 *           out by a public "Dunno" -- and marks ?? where somebody answered
 *           "No! (Has Card)" but which card is still unsettled
 *   Max     resolves those ?? by constraint propagation
 *
 * Smart and Max are exactly the two bot difficulties. That is not a
 * coincidence: under these house rules every refutation is public, so the line
 * between "what you were told" and "what follows from it" is the only line
 * there is. Max mode therefore shows precisely what the ruthless bot can see.
 *
 * In every mode a player can mark a cell themselves, and their mark sits in
 * the corner rather than replacing the algorithm's. That is the point: a ??
 * stays a ?? because the algorithm genuinely cannot know -- but you might,
 * from a hesitation or a glance across the table.
 */

const SYMBOLS = ['card', 'cross', 'query', 'flag'] as const;
export type Symbol = (typeof SYMBOLS)[number];

/**
 * Seat indices in the order their columns are drawn.
 *
 * Your own column comes first, hard against the labels. It is the fixed
 * reference every deduction starts from -- "I hold these, so..." -- and having
 * its position depend on the luck of the seat draw meant the family's muscle
 * memory reset every game.
 *
 * Everyone else follows in turn order, wrapping. So reading rightward from
 * your own column gives the exact order players answer your suggestion in,
 * which is how refutations are actually reasoned about. Spectators have no
 * seat of their own and simply get seat order.
 */
/**
 * How far to turn the column headers, in degrees, for a given player count.
 *
 * Measured in the app's own font (600 11.84px system-ui) against the classic
 * theme's longest header, "Mustard" at 44.8px, in the 340px panel:
 *
 *   4 players   67.0px per column, 60.0px usable -- 15px of slack. Upright
 *               names are comfortable, so turning them would spend header
 *               height to fix a problem that is not there.
 *   5 players   55.2px per column, 48.2px usable -- they fit, but with 3.4px
 *               to spare they butt against their own borders.
 *   6 players   47.3px per column, 40.3px usable -- "Mustard" no longer fits
 *               at all, which is where this started.
 *
 * Turning by θ costs sin θ × text width in header height, and that is paid for
 * in card rows, so each count gets the least angle that reads comfortably
 * rather than one angle imposed on all of them.
 */
function headAngle(count: number): number {
  if (count >= 6) return 45;
  if (count === 5) return 30;

  return 0;
}

function columnOrder(count: number, mySeat: number | null): number[] {
  const start = mySeat ?? 0;

  return Array.from({ length: count }, (_, i) => (start + i) % count);
}

export interface NotepadCallbacks {
  onNotesChanged: (notes: Record<string, Symbol>) => void;
  onModeChanged?: (mode: NotepadMode) => void;
}

const MODE_LABELS: Record<NotepadMode, string> = {
  manual: 'M',
  smart: 'Smart',
  max: 'Max',
};

export class Notepad {
  private mode: NotepadMode;
  private notes: Record<string, Symbol> = {};
  private grid: { smart: NotepadGrid; max: NotepadGrid } | null = null;
  private seats: Seat[] = [];
  private counts: number[] = [];

  /**
   * Seat indices, in the order their columns appear. This is a display
   * permutation only -- everything stored or sent still travels by true seat
   * index. See the note on the key in draw().
   */
  private order: number[] = [];

  /** Degrees the column headers are turned by. Zero when they fit upright. */
  private angle = 0;

  private readonly root: HTMLElement;
  private readonly table = document.createElement('div');
  private readonly toggle = document.createElement('div');

  constructor(root: HTMLElement, private readonly callbacks: NotepadCallbacks) {
    this.root = root;
    this.mode = (localStorage.getItem('clueme.notepad.mode') as NotepadMode) || 'smart';

    this.table.className = 'note-grid';
    this.toggle.className = 'mode-toggle';

    this.root.replaceChildren(this.toggle, this.table);
    this.drawToggle();
  }

  update(
    grid: { smart: NotepadGrid; max: NotepadGrid } | null,
    notes: Record<string, Symbol>,
    seats: Seat[],
    counts: number[],
    mySeat: number | null,
  ): void {
    this.grid = grid;
    this.notes = { ...notes };
    this.seats = seats;
    this.counts = counts;
    this.order = columnOrder(seats.length, mySeat);

    // More players means less room per column, so the label column has to
    // give some back. Six players in a 340px panel leaves under 40px a piece
    // at 60px of labels; narrowing the labels is what keeps that usable
    // rather than a CSS media query trying to guess player count.
    const labelWidth = seats.length <= 3 ? 60 : seats.length <= 4 ? 48 : seats.length <= 5 ? 40 : 32;
    this.table.style.setProperty('--label-col', `${labelWidth}px`);

    this.angle = headAngle(seats.length);
    this.table.style.setProperty('--head-angle', `${this.angle}deg`);

    this.draw();
  }

  private drawToggle(): void {
    this.toggle.replaceChildren(
      ...(['manual', 'smart', 'max'] as NotepadMode[]).map((mode) => {
        const button = document.createElement('button');
        button.className = `mode${mode === this.mode ? ' on' : ''}`;
        button.textContent = MODE_LABELS[mode];
        button.title =
          mode === 'manual' ? 'Mark everything yourself'
            : mode === 'smart' ? 'Fill in what you were told'
              : 'Also work out what follows';
        button.onclick = () => {
          this.mode = mode;
          localStorage.setItem('clueme.notepad.mode', mode);
          this.callbacks.onModeChanged?.(mode);
          this.drawToggle();
          this.draw();
        };
        return button;
      }),
    );
  }

  private draw(): void {
    if (!this.grid || this.seats.length === 0) {
      this.table.replaceChildren(
        Object.assign(document.createElement('p'), {
          className: 'muted',
          textContent: 'The note starts once the game does.',
        }),
      );
      return;
    }

    // Manual mode still needs the row and column structure, so it borrows the
    // smart grid's shape and simply ignores its conclusions.
    const source = this.mode === 'max' ? this.grid.max : this.grid.smart;
    const showAlgo = this.mode !== 'manual';

    const rows: HTMLElement[] = [];

    rows.push(
      this.row(
        'head',
        '',
        this.order.map((seat) => {
          const who = this.seats[seat];

          return this.cellEl('th', who?.shortName ?? who?.name ?? '');
        }),
      ),
    );

    rows.push(
      this.row(
        'counts',
        'Cards',
        this.order.map((seat) => this.cellEl('td', String(this.counts[seat] ?? 0))),
      ),
    );

    for (const section of source.sections) {
      rows.push(this.row('section', section.label, []));

      for (const line of section.rows) {
        const cells = this.order.map((seat) => {
          const state = line.cells[seat] ?? 'unknown';

          // The true seat index, never the column position. The server stores
          // a player's own marks under this key, so permuting it along with
          // the display would silently refile every annotation onto the wrong
          // player -- and quietly wrecking the most-loved feature carried over
          // from 2025 is not a trade anyone would take for column order.
          const key = `${line.cardId}|${seat}`;
          // A plain div, not a table cell: a <td> outside a real <table>
          // still carries the UA's table-cell sizing algorithm, and that
          // fought CSS Grid's row-track auto-sizing badly enough to inflate
          // every card row to 55px even though the cell's own content
          // measured a correct 17px -- the row track, not the content, was
          // wrong.
          const cell = document.createElement('div');
          cell.className = 'note-cell';

          if (showAlgo && state !== 'unknown') {
            cell.append(this.mark(state === 'has' ? 'card' : state === 'not' ? 'cross' : 'query', 'algo'));
          }

          const mine = this.notes[key];
          if (mine) {
            cell.append(this.mark(mine, 'mine'));
          }

          cell.onclick = () => this.cycle(key);
          cell.title = 'Click to add your own mark';
          return cell;
        });

        // 'note-card-row', not 'card': a bare 'card' class collided with the
        // sitewide .card utility (padding: 18px, a border, a radius) and
        // inflated every single card row to 55px. Cell content measured a
        // correct 17px the whole time; only the row wrapper was oversized --
        // which is what made this so easy to misdiagnose as a Grid/`<td>`
        // sizing bug rather than a name collision.
        rows.push(this.row(`note-card-row${line.solved ? ' solved' : ''}`, line.name, cells));
      }
    }

    this.table.replaceChildren(...rows);
    this.sizeHeaderRow();
  }

  /**
   * Give the header row enough height for its turned text.
   *
   * Measured from what the browser actually laid out rather than derived from
   * the angle, for two reasons. The longest name is a property of the theme,
   * not of the code, so a lab-theme cast with longer names would silently
   * overflow anything hardcoded -- and re-theming without touching code is the
   * point of the theme system. And trigonometry here has to agree with exactly
   * where the CSS pivots the text, which is a duplicated assumption that has
   * already been wrong once.
   *
   * The quantity measured is how far each name rises above the bottom of its
   * own cell. That does not depend on the row's height, so setting the height
   * from it cannot feed back into the measurement.
   */
  private sizeHeaderRow(): void {
    const head = this.table.querySelector('.note-row.head');
    if (!(head instanceof HTMLElement)) {
      return;
    }

    if (this.angle === 0) {
      head.style.removeProperty('height');
      this.table.style.removeProperty('--head-nudge');
      return;
    }

    // Pivoting on the bottom centre swings the line box's own centre -- half a
    // line height above the pivot -- to the left as it lifts. Undo exactly that
    // much so each name reads as centred on its column. Derived from the
    // rendered line height rather than assumed, so it holds for any font.
    const sample = head.querySelector('.turned');
    if (sample instanceof HTMLElement) {
      const drift = (sample.offsetHeight / 2) * Math.sin((this.angle * Math.PI) / 180);
      this.table.style.setProperty('--head-nudge', `${drift.toFixed(2)}px`);
    }

    let tallest = 0;
    for (const cell of head.querySelectorAll('.note-head')) {
      const turned = cell.querySelector('.turned');
      if (turned) {
        tallest = Math.max(
          tallest,
          cell.getBoundingClientRect().bottom - turned.getBoundingClientRect().top,
        );
      }
    }

    // A little clearance so the tallest ascender is not flush against whatever
    // sits above the note.
    head.style.height = `${Math.ceil(tallest) + 4}px`;
  }

  private row(kind: string, label: string, cells: HTMLElement[]): HTMLElement {
    const row = document.createElement('div');
    row.className = `note-row ${kind}`;

    const head = document.createElement('div');
    head.className = 'note-label';
    head.textContent = label;
    head.title = label;
    row.append(head);

    // A section header spans the width rather than pretending to have cells.
    if (kind === 'section') {
      row.style.setProperty('--span', String(this.seats.length));
      return row;
    }

    row.append(...cells);
    return row;
  }

  private cellEl(kind: 'th' | 'td', text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = kind === 'th' ? 'note-head' : 'note-count';
    el.title = text;

    // A turned header keeps its cell where it is and rotates only the text
    // inside -- a transform does not change layout, so rotating the cell
    // itself would take the column's borders and hit area round with it.
    if (kind === 'th' && this.angle !== 0) {
      const turned = document.createElement('span');
      turned.className = 'turned';
      turned.textContent = text;
      el.append(turned);
    } else {
      el.textContent = text;
    }

    return el;
  }

  /**
   * The algorithm's mark fills the cell; yours sits in the corner. Neither
   * overwrites the other, which is what lets you disagree with it.
   */
  private mark(symbol: Symbol, who: 'algo' | 'mine'): HTMLElement {
    const el = document.createElement('span');
    el.className = `mark ${who} sym-${symbol}`;

    if (symbol === 'card') el.textContent = '🃏';
    else if (symbol === 'cross') el.textContent = '✗';
    else if (symbol === 'query') el.textContent = '?';
    else if (symbol === 'flag') el.textContent = '⚑';

    return el;
  }

  /** blank → card → ✗ → ? → ⚑ → blank */
  private cycle(key: string): void {
    const current = this.notes[key];
    const next = current === undefined
      ? SYMBOLS[0]
      : SYMBOLS[SYMBOLS.indexOf(current) + 1];

    if (next === undefined) {
      delete this.notes[key];
    } else {
      this.notes[key] = next;
    }

    // Redraw immediately and save in the background: waiting on a round trip
    // to see your own tick makes marking a grid feel broken.
    this.draw();
    this.callbacks.onNotesChanged({ ...this.notes });
  }
}
